import type { PromptTemplate } from '@saaga/shared/interfaces/prompt-template.interface'
import { CopyIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import type { UseFormReturn } from 'react-hook-form'

import type { ParsedTemplateVariable } from '@/components/organisms/template-variable-editor/template-variable.interface'
import { TemplateVariableFieldList } from '@/components/organisms/template-variable-form/template-variable-field-list.molecule'
import type { TemplateVariableFormValues } from '@/components/organisms/template-variable-form/template-variable-form.interface'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Form } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'

import type { DeepLinkBuilderDialogProps } from './deep-link-builder-dialog.interface'

const NO_TEMPLATE_VALUE = '__none__'

interface TemplateSelectSectionProps {
  templateList: PromptTemplate[]
  selectedTemplateId: string | null
  onTemplateSelect: (templateId: string | null) => void
  isDisabled: boolean
}

const TemplateSelectSection: React.FC<TemplateSelectSectionProps> = ({
  templateList,
  selectedTemplateId,
  onTemplateSelect,
  isDisabled,
}): ReactNode => {
  const handleValueChange = (value: string): void => {
    onTemplateSelect(value === NO_TEMPLATE_VALUE ? null : value)
  }
  return (
    <div className="deep-link-builder-template-field space-y-2">
      <Label htmlFor="deep-link-builder-template-select">Template</Label>
      <Select
        value={selectedTemplateId ?? NO_TEMPLATE_VALUE}
        onValueChange={handleValueChange}
        disabled={isDisabled}
      >
        <SelectTrigger
          id="deep-link-builder-template-select"
          className="deep-link-builder-template-select w-full"
        >
          <SelectValue placeholder="Choose template" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_TEMPLATE_VALUE}>No template</SelectItem>
          {templateList.map((template) => (
            <SelectItem key={template.id} value={template.id}>
              {template.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

interface VariableFieldsSectionProps {
  parsedVariableList: ParsedTemplateVariable[]
  form: UseFormReturn<TemplateVariableFormValues>
  isDisabled: boolean
}

const VariableFieldsSection: React.FC<VariableFieldsSectionProps> = ({
  parsedVariableList,
  form,
  isDisabled,
}): ReactNode => {
  if (parsedVariableList.length === 0) return null
  return (
    <Form {...form}>
      <div className="deep-link-builder-variables flex flex-col gap-4">
        <TemplateVariableFieldList
          form={form}
          variableList={parsedVariableList}
          isDisabled={isDisabled}
        />
      </div>
    </Form>
  )
}

interface UrlPreviewSectionProps {
  urlPreview: string
  onCopy: () => void
}

const UrlPreviewSection: React.FC<UrlPreviewSectionProps> = ({ urlPreview, onCopy }): ReactNode => (
  <div className="deep-link-builder-url-field space-y-2">
    <Label htmlFor="deep-link-builder-url-preview">Link</Label>
    <div className="deep-link-builder-url-row flex items-center gap-2">
      <Input
        id="deep-link-builder-url-preview"
        readOnly
        value={urlPreview}
        className="deep-link-builder-url-preview flex-1"
        onFocus={(e) => e.currentTarget.select()}
      />
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="deep-link-builder-url-copy-button"
        onClick={onCopy}
        aria-label="Copy link"
      >
        <CopyIcon className="size-4" />
      </Button>
    </div>
  </div>
)

interface DialogBodyProps {
  templateList: PromptTemplate[]
  selectedTemplateId: string | null
  onTemplateSelect: (templateId: string | null) => void
  parsedVariableList: ParsedTemplateVariable[]
  form: UseFormReturn<TemplateVariableFormValues>
  urlPreview: string
  onCopy: () => void
  isLoading: boolean
}

const DialogBody: React.FC<DialogBodyProps> = ({
  templateList,
  selectedTemplateId,
  onTemplateSelect,
  parsedVariableList,
  form,
  urlPreview,
  onCopy,
  isLoading,
}): ReactNode => (
  <ScrollArea className="deep-link-builder-scroll max-h-[60vh]">
    <div className="deep-link-builder-body flex flex-col gap-4 pr-3">
      <TemplateSelectSection
        templateList={templateList}
        selectedTemplateId={selectedTemplateId}
        onTemplateSelect={onTemplateSelect}
        isDisabled={isLoading}
      />
      <VariableFieldsSection
        parsedVariableList={parsedVariableList}
        form={form}
        isDisabled={isLoading}
      />
      <Separator />
      <UrlPreviewSection urlPreview={urlPreview} onCopy={onCopy} />
    </div>
  </ScrollArea>
)

interface DialogFooterActionsProps {
  onCancel: () => void
  onCopy: () => void
}

const DialogFooterActions: React.FC<DialogFooterActionsProps> = ({
  onCancel,
  onCopy,
}): ReactNode => (
  <DialogFooter>
    <Button
      type="button"
      variant="ghost"
      className="deep-link-builder-cancel-button"
      onClick={onCancel}
    >
      Cancel
    </Button>
    <Button type="button" className="deep-link-builder-copy-button" onClick={onCopy}>
      Copy Link
    </Button>
  </DialogFooter>
)

export const DeepLinkBuilderDialog: React.FC<DeepLinkBuilderDialogProps> = ({
  isOpen,
  onOpenChange,
  agencyName,
  templateList,
  selectedTemplateId,
  onTemplateSelect,
  parsedVariableList,
  form,
  urlPreview,
  onCopy,
  isLoading = false,
}): ReactNode => (
  <Dialog open={isOpen} onOpenChange={onOpenChange}>
    <DialogContent className="deep-link-builder-dialog sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>Share {agencyName} link</DialogTitle>
        <DialogDescription>
          Customize the link with a template and prefilled values.
        </DialogDescription>
      </DialogHeader>
      <DialogBody
        templateList={templateList}
        selectedTemplateId={selectedTemplateId}
        onTemplateSelect={onTemplateSelect}
        parsedVariableList={parsedVariableList}
        form={form}
        urlPreview={urlPreview}
        onCopy={onCopy}
        isLoading={isLoading}
      />
      <DialogFooterActions onCancel={() => onOpenChange(false)} onCopy={onCopy} />
    </DialogContent>
  </Dialog>
)

DeepLinkBuilderDialog.displayName = 'DeepLinkBuilderDialog'
